import React from 'react';
import {defineMessages} from 'react-intl';
import {AccessControlIndicator} from '@igp/shared/access-control';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {LastUpdated} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {RealtimeMessageTrigger, RealtimeUpdatesMode} from '@igp/shared/redux/realtime';

import {withAccessCheck} from '@access-control';
import type {DebtTransferLogViewModelKeys} from '@features/debt-transfer-log';
import {
    DebtTransferLogDataGrid,
    DebtTransferLogDownloadButton,
    DebtTransferLogQueryFilter,
    debtTransferLogsPageResource,
    useDebtTransferLogs,
} from '@features/debt-transfer-log';
import {AdjustDebtButton} from '@features/debt-transfer-log';
import type {DebtTransferLogFilterName} from '@features/debt-transfer-log/types';

const localized = defineMessages({
    title: {
        id: 'DebtTransferLogs_title',
        defaultMessage: 'Debt Transfer Logs',
    },
});

function DebtTransferLogsInner() {
    const fields: DebtTransferLogViewModelKeys[] = [
        'transferDate',
        'masterAgentId',
        'masterAgentUsername',
        'agentId',
        'agentUsername',
        'transferredAmount',
    ];
    const filters: DebtTransferLogFilterName[] = ['text', 'transferDate'];
    const {
        items,
        rawItems,
        totalCount,
        filterString,
        lastUpdated,
        searchFilter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = useDebtTransferLogs({
        viewType: 'DebtTransferLogs',
        fields,
        syncWithUrl: true,
        validateFilter: () => true,
        defaultPaging: {page: 1, pageSize: 25},
        defaultSorting: [
            {
                field: 'transferDate',
                sort: 'desc',
            },
        ],
        realtimeMode: RealtimeUpdatesMode.Confirm,
        triggers: [
            {
                type: RealtimeMessageTrigger.Add,
            },
        ],
    });
    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <>
                            <AdjustDebtButton />
                            <DebtTransferLogDownloadButton
                                keys={fields}
                                filter={filterString}
                                filename={localized.title}
                                total={totalCount}
                                mode="server"
                            />
                        </>
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems primary streched>
                            <DebtTransferLogQueryFilter model={filterString} onChange={handleFilterChange} availableFilters={filters} />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <>
                    <LastUpdated value={lastUpdated} />
                    <DebtTransferLogDataGrid
                        rows={items}
                        rawModels={rawItems}
                        paging={{
                            ...searchFilter.paging,
                            rowCount: totalCount,
                        }}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        sortModel={{
                            sortModel: searchFilter?.sorting,
                        }}
                        onSortChange={handleSortChange}
                        columns={fields}
                    />
                </>
            }
        />
    );
}

const DebtTransferLogsWithAccess = withAccessCheck(DebtTransferLogsInner, debtTransferLogsPageResource, AccessControlIndicator.InfoMessage);

export const DebtTransferLogs = withTraceErrorBoundary(DebtTransferLogsWithAccess, ErrorModal);
